<script setup lang="ts">
// Second purpose-sheet step for Other. Emits only a valid selection.
import { computed, onMounted, ref } from 'vue';
import {
    OTHER_PAYMENT_DESCRIPTION_MAX_LENGTH,
    OTHER_PAYMENT_TYPES,
    OTHER_PAYMENT_TYPE_LABELS,
    type OtherPaymentSelection,
    type OtherPaymentType,
} from '@Constants/otherPaymentType.constants';

defineProps<{
    isStarting?: boolean;
}>();

const emit = defineEmits<{
    back: [];
    continue: [selection: OtherPaymentSelection];
}>();

const titleRef = ref<HTMLElement | null>(null);
const selectedType = ref<OtherPaymentType | null>(null);
const description = ref('');

// Focus moves to the new step's heading so keyboard and screen reader users land on it.
onMounted(() => {
    titleRef.value?.focus();
});

const trimmedDescription = computed(() => description.value.trim());
const needsDescription = computed(() => selectedType.value === 'Other');

const canContinue = computed(() => {
    if (selectedType.value === null) return false;
    return selectedType.value !== 'Other' || trimmedDescription.value.length > 0;
});

const descriptionMessage = computed(() =>
    needsDescription.value && description.value.length > 0 && trimmedDescription.value.length === 0
        ? 'Enter a short description of this payment, such as what it covers.'
        : null);

const submit = () => {
    const type = selectedType.value;
    if (type === null || !canContinue.value) return;
    emit('continue', type === 'Other'
        ? { type, description: trimmedDescription.value }
        : { type });
};
</script>

<template>
    <form class="other-type-step portal-step-enter" @submit.prevent="submit">
        <div class="other-type-step__header">
            <h2 ref="titleRef" class="other-type-step__title" tabindex="-1">What kind of payment is this?</h2>
            <p class="other-type-step__subtitle">Choose the option that best describes this payment.</p>
        </div>

        <fieldset class="other-type-step__fieldset">
            <legend class="other-type-step__legend">Payment type</legend>
            <div class="other-type-step__types">
                <app-radio
                    v-for="type in OTHER_PAYMENT_TYPES"
                    :key="type"
                    v-model="selectedType"
                    radio-style="outlined"
                    :native-value="type"
                    :is-disabled="isStarting"
                    :data-id="`other-type-${type}`"
                >
                    {{ OTHER_PAYMENT_TYPE_LABELS[type] }}
                </app-radio>
            </div>
        </fieldset>

        <app-text-input
            v-if="needsDescription"
            v-model="description"
            label="Describe this payment"
            placeholder="For example, a repair credit"
            :maxlength="OTHER_PAYMENT_DESCRIPTION_MAX_LENGTH"
            is-required
            :message="descriptionMessage"
            data-id="other-payment-description"
        />

        <div class="other-type-step__actions">
            <app-button
                expanded
                size="large"
                type="primary"
                :is-disabled="!canContinue || isStarting"
                :is-loading="isStarting"
                data-id="other-payment-continue"
                @click="submit"
            >
                Continue
            </app-button>
            <button
                type="button"
                class="other-type-step__back"
                data-test="other-type-back"
                :disabled="isStarting"
                @click="emit('back')"
            >
                Back
            </button>
        </div>
    </form>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.other-type-step {
    display: flex;
    flex-direction: column;
    gap: 20px;

    &__header {
        text-align: center;
        // BottomSheetModal pins its close control absolutely and reserves no space
        // for it, so the centred title has to clear it. Matches PaymentPurposeSheet.
        padding-top: 32px;

        @media (min-width: 768px) {
            padding-top: 36px;
        }
    }

    &__title {
        font-size: $text-portal-h3;
        font-weight: 700;
        line-height: 1.25;
        color: $portal-text;
        margin: 0;

        &:focus {
            outline: none;
        }
    }

    &__subtitle {
        font-size: $text-portal-caption;
        color: $portal-text-mid;
        margin: 8px 0 0;
    }

    &__fieldset {
        border: 0;
        margin: 0;
        padding: 0;
        min-width: 0;
    }

    &__legend {
        font-size: $text-portal-small;
        color: $portal-text-mid;
        padding: 0;
        margin-bottom: 8px;
    }

    &__types {
        display: flex;
        flex-direction: column;
        gap: $portal-item-gap;
    }

    &__actions {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
    }

    &__back {
        align-self: center;
        padding: 8px;
        background: none;
        border: 0;
        // Buttons don't inherit the page font-family by default.
        font-family: inherit;
        font-size: $text-portal-caption;
        font-weight: 700;
        color: $theme-primary;
        cursor: pointer;

        &:focus-visible {
            outline: 2px solid $theme-primary;
            outline-offset: 2px;
        }

        &:disabled {
            opacity: 0.6;
            cursor: default;
        }
    }
}
</style>
